<template>
<div>
  <ul
    v-if="items && items.length > 1 && indicatorType !== 'navigation'"
    v-show="show"
    class="vch-carousel-indicator text-small"
    :class="`vch-carousel-indicator--${indicatorType}`"
    :aria-label="ariaPaginationLabel"
  >
    <li
      v-for="({ id }, index) in items"
      :key="id"
      class="vch-carousel-indicator__item"
    >
      <a
        v-if="useAnchorScrolling"
        :href="`#${id}`"
        :class="[
          'vch-carousel-indicator__link',
          Math.abs(activeIndicator) === Math.abs(parseInt(index)) && '--active',
        ]"
        @click="pauseVideo"
      >
        <span>{{ prefixNumber(Math.abs(parseInt(index))) }}</span>
      </a>
      <button
        v-else
        :class="[
          'vch-carousel-indicator__link',
          Math.abs(activeIndicator) === Math.abs(parseInt(index)) && '--active',
        ]"
        :aria-current="Math.abs(activeIndicator) === Math.abs(parseInt(index))"
        @click="() => onIndicatorButtonClick(Math.abs(parseInt(index)))"
        @keyup.enter="onIndicatorButtonClick(Math.abs(parseInt(index)))"
      >
        <span>{{ prefixNumber(Math.abs(parseInt(index))) + ' - ' + ariaItemLabel }}</span>
      </button>
    </li>
  </ul>
  <div v-if="items && items.length > 1 && indicatorType === 'navigation' && isMobile" class="nav-controls">
      <button
        v-if="currentItem  > 0"
        @click="goTo(-1)"
        class="btn-prev">
          <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.85" d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#F6F6F6"/>
          <path d="M17.1211 19.0511L23.8959 25.3465L22.8798 26.2905L16.105 19.9951L17.1211 19.0511Z" fill="black" stroke="black" stroke-width="0.5"/>
          <path d="M16.1052 20.0053L22.88 13.71L23.8954 14.6547L17.1213 20.9493L16.1052 20.0053Z" fill="black" stroke="black" stroke-width="0.5"/>
          </svg>
      </button>
      <button
        v-if="currentItem  < items.length - 1"
        @click="goTo(1)"
        class="btn-next">
        <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path opacity="0.85" d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z" fill="#F6F6F6"></path>
        <path d="M22.8789 19.0511L16.1041 25.3465L17.1202 26.2905L23.895 19.9951L22.8789 19.0511Z" fill="black" stroke="black" stroke-width="0.5"></path>
        <path d="M23.8948 20.0053L17.12 13.71L16.1046 14.6547L22.8787 20.9493L23.8948 20.0053Z" fill="black" stroke="black" stroke-width="0.5"></path>
        </svg>
      </button>
  </div>
  <div v-if="items && items.length > 1 && indicatorType === 'bar'" class="navigation_mixed_slider">
    <button v-show="currentItem > 0" @click="goToPrevious" @keyup.enter="goToPrevious" aria-label="Previous slide" tabindex="0" class="btn-prev">
        <svg width="40" height="40" role="img" aria-label="Previous" class="svg_nav">
          <use href="/assets/sprite/icons.svg#btn-next"></use>
        </svg>
    </button>
    <button v-show="currentItem < items.length - 1" @click="goToNext" @keyup.enter="goToNext" aria-label="Next slide" tabindex="0" class="btn-next">
       <svg width="40" height="40" role="img" aria-label="Next" class="svg_nav">
          <use href="/assets/sprite/icons.svg#btn-next"></use>
       </svg>
    </button>
  </div>
  </div>
</template>

<script>
let observer;
const indicatorTypes = ['numbers', 'dots', 'bar','navigation'];

export default {
  name: 'vch-carousel-indicator',

  props: {
    indicatorType: {
      type: String,
      required: false,
      default: indicatorTypes[0],
      validator: (value) => {
        return !!value && indicatorTypes.includes(value);
      },
    },
    itemSelector: {
      type: String,
      required: true,
    },
    mediaQueryList: {
      type: String,
      default: '(min-width: 0px)',
    },
    ariaPaginationLabel: {
      type: String,
      default: 'Pagination',
    },
    ariaItemLabel: {
      type: String,
      default: 'Picture',
    },
    intersectionThreshold: {
      type: Number,
      default: 0.9,
      required: false,
    },
    observeContent: {
      type: Boolean,
      default: true,
      required: false,
    },
    currentItem: {
      type: Number,
      required: false,
      default: 0,
    },
    useAnchorScrolling: {
      type: Boolean,
      default: true,
      required: false,
    },
    onIndicatorClick: {
      type: Function,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      show: false,
      items: null,
      intersectingItemIndex: 0,
      mql: null,
      isMobile: window.innerWidth <= 768,
    };
  },
  watch: {
    intersectingItemIndex(newIndex) {
      const payload = parseInt(newIndex) === 0 ? 0 : 1;
      window.eventBus.$emit('vch-remove-zoom-icon', payload);
    }
  },
  computed: {
    activeIndicator() {
      window.eventBus.$emit('vch-remove-zoom-icon', this.intersectingItemIndex);
      return this.observeContent
        ? parseInt(this.intersectingItemIndex)
        : this.currentItem;
    },
  },
  mounted() {
    this.items = this.getItems();

    if (this.items.length >= 1) {
      this.mql = window.matchMedia(this.mediaQueryList);
      if (this.observeContent) {
        observer = new IntersectionObserver(this.updateIntersectingItemIndex, {
          root: document.body,
          threshold: this.intersectionThreshold,
        });
      }
      this.toggleIndicator(this.mql);
      this.mql.addEventListener('change', this.toggleIndicator);
    }
  },
  beforeDestroy() {
    if (this.observeContent) {
      this.stopObserver();
    }
    if (this.mql) {
      this.mql.removeEventListener('change', this.toggleIndicator);
    }
  },
  methods: {
    getItems() {
      const parentElement = this.$el.parentElement;
      if (parentElement) {
        return Array.from(
          parentElement.querySelectorAll(this.itemSelector)
        ).filter((el) =>
          el.hasAttribute('hidden')
            ? !JSON.parse(el.getAttribute('hidden'))
            : true
        );
      }
      return document.querySelectorAll(this.itemSelector);
    },

    toggleIndicator({ matches }) {
      this.show = matches;
      if (this.observeContent) {
        if (this.show) {
          this.startObserver();
        } else {
          this.stopObserver();
        }
      }
    },

    updateIntersectingItemIndex([e]) {
      if (e.isIntersecting) {
        const index = Array.prototype.indexOf.call(this.items, e.target);
        this.intersectingItemIndex = index.toString();
        if (index !== -1) {
          this.$emit('update:currentItem', index);
        }
      }
    },

    startObserver() {
      this.items.forEach((item) => {
        observer.observe(item);
      });
    },

    stopObserver() {
      this.items.forEach((item) => {
        observer.unobserve(item);
      });
    },

    prefixNumber(num) {
      return num < 10 ? `0${num + 1}` : `${num + 1}`;
    },
    goToNext() {
      if (!this.observeContent) {
        const nextIndex = (this.currentItem + 1) % this.items.length;
        this.onIndicatorButtonClick(nextIndex);
      }
    },
    goToPrevious() {
      if (!this.observeContent) {
        const prevIndex = (this.currentItem - 1 + this.items.length) % this.items.length;
        this.onIndicatorButtonClick(prevIndex);
      }
    },
    onIndicatorButtonClick(index) {
      if (!this.observeContent && index !== this.currentItem) {
        this.currentItem = index;
        if (this.onIndicatorClick) this.onIndicatorClick(index);
      }
    },
    pauseVideo() {
      let iframes = document.querySelectorAll('.youtube-player');
      if (iframes) {
        for (var i = 0; i < iframes.length; i++) {
          var iframe = iframes[i];
          var temp = iframe.src;
          iframe.src = temp;
        }
      }
    },
    goTo(direction) {
      const newIndex = this.currentItem + direction;
      if (newIndex < 0 || newIndex >= this.items.length) return;

      const el = document.getElementById(this.items[newIndex]?.id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });

      this.intersectingItemIndex = newIndex.toString();
      this.$emit('update:currentItem', newIndex);
    }
  },
};
</script>

<style lang="scss" src="./carousel-indicator.scss"></style>
